import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, ApiClientError } from '@client/lib/api';
import { formatCents } from '@shared/money';
import styles from './PublicInvoicePage.module.css';

interface PublicItem {
  description: string;
  quantity: number;
  unitPriceCents: number;
  lineTotalCents: number;
}

interface BusinessInfo {
  name: string;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

interface PublicInvoiceData {
  amountPaidCents: number;
  balanceDueCents: number;
  business: BusinessInfo;
  customerLabel: string;
  customerMessage: string | null;
  dueDate: string | null;
  issueDate: string;
  items: PublicItem[];
  kind: 'INVOICE';
  number: string;
  propertyAddress: string | null;
  status: string;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
}

/**
 * Public invoice page — accessible without authentication.
 *
 * Rendered outside the authenticated route tree so a customer with no account
 * can view their invoice. Any redirect to the sign-in page is a bug.
 */
export function PublicInvoicePage() {
  const { token = '' } = useParams<{ token: string }>();
  const [invoice, setInvoice] = useState<PublicInvoiceData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await apiGet<PublicInvoiceData>(
          `/public/invoices/${encodeURIComponent(token)}`
        );
        if (!cancelled) {
          setInvoice(data);
        }
      } catch (e) {
        if (!cancelled) {
          if (e instanceof ApiClientError && e.status === 404) {
            setError('not-found');
          } else {
            setError('error');
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return <div className={styles.loading}>Loading invoice…</div>;
  }

  if (error === 'not-found') {
    return (
      <div className={styles.notFound}>
        <h1 className={styles.notFoundTitle}>This link is no longer valid</h1>
        <p className={styles.notFoundText}>Ask the business for a new one.</p>
      </div>
    );
  }

  if (error === 'error') {
    return (
      <div className={styles.notFound}>
        <h1 className={styles.notFoundTitle}>Something went wrong</h1>
        <p className={styles.notFoundText}>Please try again or contact the business directly.</p>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const addressParts = [
    invoice.business.addressLine1,
    invoice.business.addressLine2,
    [invoice.business.city, invoice.business.state, invoice.business.postalCode]
      .filter(Boolean)
      .join(', '),
  ].filter(Boolean);

  const statusLabel =
    invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1).toLowerCase();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Invoice {invoice.number}</h1>
        <p className={styles.subtitle}>for {invoice.customerLabel}</p>
      </div>

      <div className={styles.businessInfo}>
        <p className={styles.businessName}>{invoice.business.name}</p>
        {addressParts.length > 0 && (
          <p className={styles.businessAddress}>{addressParts.join(', ')}</p>
        )}
      </div>

      {invoice.customerMessage && (
        <div className={styles.customerMessage}>
          <strong>Message:</strong> {invoice.customerMessage}
        </div>
      )}

      <div className={styles.status}>{statusLabel}</div>

      {invoice.propertyAddress && (
        <div className={styles.propertyAddress}>{invoice.propertyAddress}</div>
      )}

      <h2 className={styles.sectionTitle}>Line items</h2>
      {invoice.items.length === 0 ? (
        <p className={styles.empty}>No line items.</p>
      ) : (
        <ul className={styles.lineItems}>
          {invoice.items.map((item, index) => (
            <li key={index} className={styles.lineItem}>
              <span className={styles.lineItemDescription}>{item.description}</span>
              <span className={styles.lineItemDetails}>
                {item.quantity} × {formatCents(item.unitPriceCents)}
              </span>
              <span className={styles.lineItemTotal}>{formatCents(item.lineTotalCents)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.totals}>
        <div className={styles.totalsRow}>
          <span>Subtotal</span>
          <span>{formatCents(invoice.subtotalCents)}</span>
        </div>
        <div className={styles.totalsRow}>
          <span>Tax</span>
          <span>{formatCents(invoice.taxCents)}</span>
        </div>
        <div className={styles.totalsRow}>
          <span>Total</span>
          <span>{formatCents(invoice.totalCents)}</span>
        </div>
        <div className={styles.totalsRow}>
          <span>Amount paid</span>
          <span>{formatCents(invoice.amountPaidCents)}</span>
        </div>
        <div className={styles.totalsRow}>
          <span>Balance due</span>
          <span>{formatCents(invoice.balanceDueCents)}</span>
        </div>
      </div>

      {invoice.dueDate && <div className={styles.expiry}>Due date: {invoice.dueDate}</div>}
    </div>
  );
}
